import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ChevronDown, Share2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { StatusBadge } from "../components/StatusBadge";
import { Reviews } from "../components/Reviews";
import { MainAccords } from "../components/MainAccords";
import { FragranceProfile } from "../components/FragranceProfile";
import { WearGuide } from "../components/WearGuide";
import { NotifyMe } from "../components/NotifyMe";
import { fmtPrice } from "../lib/format";
import api, { resolveImg } from "../lib/api";
import { toast } from "sonner";

const Accordion = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#262421]">
      <button onClick={() => setOpen(!open)} data-testid={testid} className="w-full flex items-center justify-between py-5 text-start">
        <span className="text-xs uppercase tracking-[0.2em] text-[#F2EBE1]">{title}</span>
        <ChevronDown size={18} className={`text-[#8A857D] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-6 text-sm text-[#8A857D] leading-relaxed">{children}</div>}
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, loc, lang, money } = useLang();
  const { addItem, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => { setProduct(r.data); setActive(0); setQty(1); }).catch(() => setProduct(false));
  }, [slug]);

  if (product === false) return <div className="py-40 text-center text-[#8A857D]">Not found. <Link to="/shop" className="text-[#C49A6C]">{t("nav_shop")}</Link></div>;
  if (!product) return <div className="py-40 text-center text-[#8A857D]">…</div>;

  const buyable = product.status !== "coming_soon" && product.stock > 0;
  const soldOut = product.status === "sold_out" || (product.status !== "coming_soon" && product.stock <= 0);
  const notes = lang === "ar" ? product.notes_ar : product.notes_en;
  const olfactive = loc(product, "olfactive_family");
  const scentProfile = loc(product, "scent_profile");
  const ingredientsRaw = loc(product, "ingredients");
  const ingList = ingredientsRaw ? ingredientsRaw.split(/[,\n،]+/).map((s) => s.trim()).filter(Boolean) : [];
  const description = loc(product, "description");
  const naturalPct = product.natural_percentage;

  const add = () => {
    if (qty > product.stock) { toast.error(t("max_stock")); return; }
    addItem(product, qty);
    toast.success(t("added_to_bag"));
    setOpen(true);
  };
  const buyNow = () => {
    if (qty > product.stock) { toast.error(t("max_stock")); return; }
    addItem(product, qty);
    navigate("/checkout");
  };
  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: product.name_en, url });
    else { navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-16">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <motion.div key={active} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="aspect-[4/5] bg-[#141311] overflow-hidden relative">
            <img src={resolveImg(product.images?.[active])} alt={loc(product, "name")} className={`w-full h-full object-cover ${soldOut ? "grayscale brightness-75" : ""}`} data-testid="product-main-image" />
            {soldOut && (
              <div className="absolute inset-0 flex items-center justify-center bg-[#0B0A08]/35" data-testid="product-sold-out-overlay">
                <div className="text-center">
                  <p className="serif italic text-[#F2EBE1] text-5xl md:text-6xl tracking-wide drop-shadow-lg">Sold Out</p>
                  <p className="text-[11px] uppercase tracking-[0.4em] text-[#C49A6C] mt-3">{lang === "ar" ? "نفدت الكمية" : "Fully Reserved"}</p>
                </div>
              </div>
            )}
          </motion.div>
          {product.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {product.images.map((img, i) => (
                <button key={img} onClick={() => setActive(i)} data-testid={`thumb-${i}`} className={`aspect-square bg-[#141311] overflow-hidden border ${active === i ? "border-[#C49A6C]" : "border-transparent"}`}>
                  <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center justify-between">
            {olfactive ? (
              <span data-testid="olfactive-family" className="text-[10px] md:text-xs uppercase tracking-[0.32em] text-[#C49A6C]">{olfactive}</span>
            ) : <StatusBadge status={product.status} />}
            <button onClick={share} data-testid="share-btn" className="text-[#8A857D] hover:text-[#C49A6C] transition-colors flex items-center gap-2 text-xs uppercase tracking-widest">
              <Share2 size={15} strokeWidth={1.4} /> Share
            </button>
          </div>

          <h1 className="serif text-5xl md:text-6xl mt-4 leading-[0.95] tracking-tight" data-testid="product-name">{loc(product, "name")}</h1>

          {scentProfile ? (
            <p data-testid="scent-profile" className="serif italic text-2xl md:text-3xl text-[#C49A6C]/90 mt-6 leading-snug">{scentProfile}</p>
          ) : (
            <p className="text-[#8A857D] leading-relaxed mt-6 text-lg">{loc(product, "short")}</p>
          )}

          <div className="grid grid-cols-3 gap-4 mt-9 border-y border-[#262421] py-6">
            <div><p className="text-[10px] uppercase tracking-widest text-[#6E685F]">{t("size")}</p><p className="text-sm mt-1.5">{product.size}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-[#6E685F]">{t("concentration")}</p><p className="text-sm mt-1.5">{product.concentration}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest text-[#6E685F]">{t("availability")}</p><p className="text-sm mt-1.5">{buyable ? `${product.stock} ${t("units_left")}` : t(product.status === "coming_soon" ? "coming_soon" : "sold_out")}</p></div>
          </div>

          {notes?.length > 0 && (
            <div className="mt-8">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#6E685F] mb-3">{t("notes")}</p>
              <div className="flex flex-wrap gap-2">
                {notes.map((n, i) => (
                  <span key={n} className="text-xs uppercase tracking-[0.15em] text-[#C49A6C] border border-[#262421] px-3 py-1.5">{n}</span>
                ))}
              </div>
            </div>
          )}

          <p className="serif text-4xl text-[#F2EBE1] mt-9" data-testid="product-price">{money(product.price)}</p>

          <MainAccords accords={product.accords} />
          <FragranceProfile product={product} />
          <WearGuide product={product} />

          {buyable && (
            <div className="mt-4 flex items-center gap-2.5" data-testid="stock-scarcity">
              <span className={`inline-block w-2 h-2 rounded-full ${product.stock <= 10 ? "bg-[#C6553B]" : "bg-[#7F9E6B]"}`} />
              <p className={`text-xs uppercase tracking-[0.2em] ${product.stock <= 10 ? "text-[#E0A38F]" : "text-[#8A857D]"}`}>
                {t("only_left_pre")} {product.stock} {t("only_left_post")}
              </p>
            </div>
          )}

          {buyable && (
            <div className="flex items-center gap-4 mt-8">
              <span className="text-xs uppercase tracking-widest text-[#8A857D]">{t("quantity")}</span>
              <div className="flex items-center border border-[#262421]">
                <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-dec" className="px-3 py-2 text-[#8A857D] hover:text-[#F2EBE1]"><Minus size={15} /></button>
                <span className="px-4 text-sm" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(Math.min(product.stock, qty + 1))} data-testid="qty-inc" className="px-3 py-2 text-[#8A857D] hover:text-[#F2EBE1]"><Plus size={15} /></button>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            {buyable ? (
              <>
                <button onClick={add} data-testid="add-to-bag-btn" className="flex-1 border border-[#C49A6C] text-[#C49A6C] text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#C49A6C] hover:text-[#0B0A08] transition-colors">
                  {t("add_to_bag")}
                </button>
                <button onClick={buyNow} data-testid="buy-now-btn" className="flex-1 bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#E7CBA9] transition-colors">
                  {t("buy_now")}
                </button>
              </>
            ) : (
              <div className="flex-1 border border-[#262421] text-[#6E685F] text-xs uppercase tracking-[0.25em] py-4 text-center">
                {t(product.status === "coming_soon" ? "coming_soon" : "sold_out")}
              </div>
            )}
          </div>
          {!buyable && <NotifyMe slug={product.slug} />}

          {/* Ingredients */}
          {ingList.length > 0 && (
            <div className="mt-14" data-testid="ingredients-section">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C49A6C] mb-4">{t("ingredients")}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {ingList.map((ing, i) => (
                  <span key={ing} className="flex items-center gap-3 text-[#E7CBA9]/90 serif text-lg">
                    {i > 0 && <span className="w-1 h-1 rounded-full bg-[#C49A6C]/50" />}
                    {ing}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* The Composition / description */}
          {description && (
            <div className="mt-14" data-testid="composition-section">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C49A6C] mb-4">{t("fragrance_description")}</p>
              <p className="text-[15px] text-[#8A857D] leading-[1.9]">{description}</p>
            </div>
          )}

          {/* Natural Materials % — centerpiece */}
          {naturalPct != null && naturalPct !== "" && (
            <div className="mt-16 border border-[#262421] bg-[#141311]/60 backdrop-blur-sm p-8 md:p-10 flex items-center gap-8" data-testid="natural-materials">
              <div className="relative shrink-0" style={{ width: 132, height: 132 }}>
                <svg viewBox="0 0 132 132" className="w-full h-full -rotate-90">
                  <circle cx="66" cy="66" r="58" fill="none" stroke="#262421" strokeWidth="3" />
                  <circle cx="66" cy="66" r="58" fill="none" stroke="#C49A6C" strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 58}
                    strokeDashoffset={2 * Math.PI * 58 * (1 - Math.min(100, Math.max(0, Number(naturalPct))) / 100)}
                    style={{ transition: "stroke-dashoffset 1.2s ease" }} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="serif text-4xl text-[#F2EBE1]">{Number(naturalPct)}<span className="text-2xl text-[#C49A6C]">%</span></span>
                </div>
              </div>
              <div>
                <p className="serif text-2xl md:text-3xl text-[#F2EBE1] leading-tight">{t("natural_materials")}</p>
                <p className="text-sm text-[#8A857D] leading-relaxed mt-3 max-w-xs">{t("natural_materials_sub")}</p>
              </div>
            </div>
          )}

          <div className="mt-14">
            <Accordion title={t("shipping_info")} testid="acc-shipping">{loc(product, "shipping")}</Accordion>
          </div>

          {product.videos?.length > 0 && (
            <div className="mt-14" data-testid="product-videos">
              <p className="serif text-2xl md:text-3xl text-[#F2EBE1] leading-tight mb-5" data-testid="product-videos-title">{(lang === "ar" ? product.videos_title_ar : product.videos_title_en) || (lang === "ar" ? "من الاستوديو" : "From the Studio")}</p>
              <div className="space-y-4">
                {product.videos.map((v, i) => (
                  /\.(mp4|webm|mov)(\?|$)/i.test(v)
                    ? <video key={v} src={resolveImg(v)} controls playsInline className="w-full bg-[#141311]" data-testid={`product-video-${i}`} />
                    : <img key={v} src={resolveImg(v)} alt="" className="w-full bg-[#141311]" data-testid={`product-media-${i}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Reviews slug={slug} />
    </div>
  );
}
